import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Tabs from "aldoo-ra/Tabs"
import { showNotification } from "aldoo-ra/Notification"
import { CreateModel, UpdateModel } from "./api"
import { usePacksData } from "./hooks/usePacksData"

export default function AddModelDialog({
  isOpen,
  model,
  onClose,
  onModelSaved,
}) {
  const [formData, setFormData] = useState({
    name: "",
    codePattern: "XXXX-XXXX",
    codeSet: "A-Z,0-9",
    explicitCode: "",
    codeGenerationType: "generated",
    keyPack: "",
    seats: 1,
    users_per_license: 0,
    validity: 0,
    primary: false,
    external: false,
  })
  const [validityType, setValidityType] = useState("lifetime")
  const [useExplicitCode, setUseExplicitCode] = useState(false)
  const [generationMode, setGenerationMode] = useState("generated")
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  // Use centralized packs data hook
  const {
    availablePacks,
    packsLoading,
    loadAvailablePacks,
    getPackOptions,
    getSelectedPackValue,
    getPackById
  } = usePacksData()


  // Reset form when dialog opens/closes or model changes
  useEffect(() => {
    if (isOpen) {
      if (model) {
        // Editing existing model
        const hasExplicitCode = Boolean(model.explicitCode)
        
        // Set generation mode based on codeGenerationType or fallback to explicitCode logic
        let generationMode = "generated"
        let finalCodeGenerationType = model.codeGenerationType || "generated"
        
        if (model.codeGenerationType) {
          if (model.codeGenerationType === "custom") {
            generationMode = "manual"
          } else if (model.codeGenerationType === "keyPack") {
            generationMode = "keyPack"
          } else {
            generationMode = "generated"
          }
        } else {
          // Fallback for older models without codeGenerationType
          generationMode = hasExplicitCode ? "manual" : "generated"
          finalCodeGenerationType = hasExplicitCode ? "custom" : "generated"
        }
        
        setFormData({
          name: model.name || "",
          codePattern: model.codePattern || "XXXX-XXXX",
          codeSet: model.codeSet || "A-Z,0-9",
          explicitCode: model.explicitCode || "",
          codeGenerationType: finalCodeGenerationType,
          keyPack: model.keyPack || "",
          seats: model.seats || 1,
          users_per_license: model.users_per_license ?? 0,
          validity: model.validity || 0,
          primary: model.primary || false,
          external: model.external || false,
        })
        setValidityType(model.validity === 0 ? "lifetime" : "days")
        setUseExplicitCode(hasExplicitCode)
        setGenerationMode(generationMode)
      } else {
        // Creating new model
        setFormData({
          name: "",
          codePattern: "XXXX-XXXX",
          codeSet: "A-Z,0-9",
          explicitCode: "",
          codeGenerationType: "generated",
          keyPack: "",
          seats: 1,
          users_per_license: 0,
          validity: 0,
          primary: false,
          external: false,
        })
        setValidityType("lifetime")
        setUseExplicitCode(false)
        setGenerationMode("generated")
        
      }
      setErrors({})
    }
  }, [isOpen, model]) // Remove loadAvailablePacks from dependencies

  // Load packs when dialog opens (separate effect to avoid dependency issues)
  useEffect(() => {
    if (isOpen) {
      loadAvailablePacks(true)
    }
  }, [isOpen, loadAvailablePacks])

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value }
      
      // Business logic: external models cannot be primary
      if (field === "external" && value === true) {
        newData.primary = false
      }
      
      return newData
    })
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }))
    }
  }

  // Handle validity type change
  const handleValidityTypeChange = (type) => {
    setValidityType(type)
    if (type === "lifetime") {
      setFormData((prev) => ({ ...prev, validity: 0 }))
    }
  }

  // Handle generation mode change
  const handleGenerationModeChange = (mode) => {
    setGenerationMode(mode)
    setUseExplicitCode(mode === "manual")
    
    // Update formData codeGenerationType based on mode
    let codeGenerationType = "generated"
    if (mode === "manual") codeGenerationType = "custom"
    else if (mode === "keyPack") codeGenerationType = "keyPack"
    
    setFormData((prev) => ({ 
      ...prev, 
      codeGenerationType,
      // Keep keyPack value preserved across all modes
      // (Don't clear keyPack when switching away from keyPack mode)
      // Disable primary when switching to manual or keyPack mode, or if external is true
      primary: (mode === "generated" && !prev.external) ? prev.primary : false
    }))
  }

  // Handle explicit code toggle (readonly, controlled by tabs)
  const handleExplicitCodeToggle = () => {
    // This is readonly, controlled by tab switching
    return
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Model name is required"
    }

    // Validate codeGenerationType
    if (!formData.codeGenerationType) {
      newErrors.codeGenerationType = "Code generation type is required"
    }

    // Validate fields based on generation type
    if (formData.codeGenerationType === "generated") {
      if (!formData.codePattern.trim()) {
        newErrors.codePattern = "Code pattern is required"
      } else if (!formData.codePattern.includes("X")) {
        newErrors.codePattern = "Code pattern must contain 'X' placeholders"
      }

      if (!formData.codeSet.trim()) {
        newErrors.codeSet = "Code set is required"
      }
    } else if (formData.codeGenerationType === "keyPack") {
      if (!formData.keyPack) {
        newErrors.keyPack = "Key pack selection is required"
      }
    }


    if (formData.seats < 1) {
      newErrors.seats = "Seats must be at least 1"
    }

    if (
      formData.users_per_license !== undefined &&
      formData.users_per_license !== "" &&
      (isNaN(Number(formData.users_per_license)) || Number(formData.users_per_license) < 0)
    ) {
      newErrors.users_per_license = "Users per License must be 0 (unlimited) or a positive integer"
    }

    if (validityType === "days" && formData.validity < 1) {
      newErrors.validity = "Validity must be at least 1 day"
    }

    // Validate external and primary mutual exclusion
    if (formData.external && formData.primary) {
      newErrors.primary = "External models cannot be set as primary"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const modelData = {
        ...formData,
        explicitCode: generationMode === "manual",
        validity: validityType === "lifetime" ? 0 : formData.validity,
        primary: (generationMode === "generated" && !formData.external) ? formData.primary : false,
        // Include new fields
        codeGenerationType: formData.codeGenerationType,
        keyPack: formData.codeGenerationType === "keyPack" ? formData.keyPack : undefined,
        external: formData.external,
      }

      let response
      if (model && model._id) {
        // Update existing model
        response = await UpdateModel({
          modelId: model._id,
          ...modelData,
        })
      } else {
        // Create new model
        response = await CreateModel(modelData)
      }

      if (response.result) {
        showNotification({
          message: `Model ${model ? "updated" : "created"} successfully`,
          className: "bg-admin_primary text-white",
        })
        onModelSaved()
      } else {
        showNotification({
          message:
            response.message || response.error || `Failed to ${model ? "update" : "create"} model`,
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Extract error message from different possible sources
      let errorMessage = `An error occurred while ${model ? "updating" : "creating"} model`
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        const responseData = error.response.data
        
        // Handle specific backend validation errors
        if (responseData.error === "external_cannot_be_primary") {
          errorMessage = "External models cannot be set as primary. Please uncheck the primary option."
        } else if (responseData.error === "invalid_external") {
          errorMessage = "Invalid external field value. Please check your input."
        } else {
          errorMessage = responseData.message || responseData.error || error.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showNotification({
        message: errorMessage,
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  // Generate sample code based on pattern
  const generateSampleCode = () => {
    const pattern = formData.codePattern || "XXXX-XXXX"
    const codeSet = formData.codeSet || "A-Z,0-9"

    // Parse character set
    let chars = ""
    const sets = codeSet.split(",")
    sets.forEach((set) => {
      if (set.trim() === "A-Z") chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
      else if (set.trim() === "0-9") chars += "0123456789"
      else if (set.trim() === "a-z") chars += "abcdefghijklmnopqrstuvwxyz"
    })

    if (!chars) return "XXXX-XXXX"

    return pattern.replace(/X/g, () => {
      return chars.charAt(Math.floor(Math.random() * chars.length))
    })
  }

  const sampleCode = generateSampleCode()

  if (!isOpen) return null

  return (
    <Modal
      variant="admin_centered"
      isOpen={isOpen}
      onClose={onClose}
      title={model ? "Edit License Model" : "Add License Model"}
      className="max-w-[690px] h-[93vh] !overflow-hidden"
      footerContent={
        <div className="flex justify-end space-x-3">
          <Button 
            variant="admin_secondary" 
            onClick={onClose} 
            disabled={loading}
          >
            Cancel
          </Button>

          <Button 
            variant="admin_primary" 
            onClick={handleSave} 
            loading={loading}
          >
            {model ? "Update Model" : "Create Model"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Model Name */}
        <div>
          <Typography
            variant="admin_label"
            className="mb-1"
          >
            Model Name
          </Typography>

          <Input
            variant="admin"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="e.g. Family Plan Model"
            error={errors.name}
            className="w-full"
          />
        </div>

        {/* Generation Mode Tabs */}
        <div>
          <Typography 
            component="h2"
            variant="admin_label"
            className="text-[18px]"
          >
            License Code Generation
          </Typography>

          <Tabs
            variant="admin"
            value={generationMode}
            onChange={handleGenerationModeChange}
            tabs={[
              { value: "generated", label: "Generated" },
              { value: "manual", label: "Custom" },
              { value: "keyPack", label: "Key Pack" },
            ]}
            className="mb-4"
          />

          {/* Generated Mode Content */}
          {generationMode === "generated" && (
            <div className="space-y-4">
              {/* Code Pattern & Code Set */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography
                    component="h3"
                    variant="admin_label"
                    className="mb-1"
                  >
                    Code Pattern
                  </Typography>

                  <Input
                    variant="admin"
                    value={formData.codePattern}
                    onChange={(e) => handleInputChange("codePattern", e.target.value)}
                    placeholder="XXXX-XXXX-XXXX"
                    error={errors.codePattern}
                    className="w-full font-mono"
                  />

                  <Typography variant="admin_txt1_dim">
                    Use 'X' for random characters, other characters will be preserved
                  </Typography>
                </div>

                <div>
                  <Typography
                    component="h3"
                    variant="admin_label"
                    className="mb-1"
                  >
                    Code Set
                  </Typography>

                  <DropDownSelect
                    variant="admin"
                    options={[
                      { value: "A-Z,0-9", label: "Letters & Numbers (A-Z,0-9)" },
                      { value: "A-Z", label: "Letters Only (A-Z)" },
                      { value: "0-9", label: "Numbers Only (0-9)" },
                      { value: "A-Z,a-z,0-9", label: "Mixed Case & Numbers" },
                    ]}
                    value={[{ value: formData.codeSet, label: formData.codeSet }]}
                    onChange={(values) =>
                      handleInputChange("codeSet", values?.[0]?.value || "A-Z,0-9")
                    }
                    className="w-full"
                    error={errors.codeSet}
                  />
                </div>
              </div>

              {/* Sample Code Preview */}
              <div className="bg-admin_canvas p-4 rounded-lg">
                <Typography 
                  component="h3"
                  variant="admin_label"
                >
                  Sample Code Preview
                </Typography>

                <Typography
                  component="output"
                  variant="admin_h4"
                  className="font-mono text-admin_primary"
                >
                  {sampleCode}
                </Typography>
              </div>

              <Typography variant="admin_txt1_dim">
                License codes will be automatically generated using the pattern and character set defined above.
              </Typography>
            </div>
          )}

          {/* Custom Mode Content */}
          {generationMode === "manual" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-3">
                <Checkbox
                  variant="admin"
                  isOn={useExplicitCode}
                  onChange={handleExplicitCodeToggle}
                  label="Use Custom License Codes"
                  disabled={true}
                />
              </div>

              <Typography variant="admin_txt1_dim">
                In manual mode, you can specify custom license codes instead of using auto-generated ones. This is useful for 3rd party integrations or when you need predictable license codes.
              </Typography>
            </div>
          )}

          {/* Key Pack Mode Content */}
          {generationMode === "keyPack" && (
            <div>
              <div className="mb-2">
                <Typography
                  component="h3"
                  variant="admin_label"
                  className="mb-2"
                >
                  Select License Pack
                </Typography>

                <DropDownSelect
                  variant="admin"
                  options={getPackOptions()}
                  value={getSelectedPackValue(formData.keyPack)}
                  onChange={(values) =>
                    handleInputChange("keyPack", values?.[0]?.value || "")
                  }
                  className="w-full"
                  error={errors.keyPack}
                  loading={packsLoading}
                  placeholder={packsLoading ? "Loading packs..." : availablePacks.length === 0 ? "No packs available" : "Select a license pack..."}
                  disabled={packsLoading || availablePacks.length === 0}
                  searchEnabled
                />
              </div>

              <Typography variant="admin_txt1_dim">
                License codes will be drawn from the selected pack and automatically activated when assigned to users. The pack must have available licenses for code generation to work.
              </Typography>
            </div>
          )}
        </div>

        {/* Seats - Only show for generated mode */}
        {generationMode === "generated" && (
          <div>
            <Typography
              component="h3"
              variant="admin_label"
              className="mb-1"
            >
              Seats per License
            </Typography>

            <Input
              variant="admin"
              type="number"
              value={formData.seats}
              onChange={(e) =>
                handleInputChange("seats", e.target.value)
              }
              min={1}
              error={errors.seats}
              className="w-full max-w-xs"
            />
          </div>
        )}

        {/* Users per License - Only show for generated mode */}
        {generationMode === "generated" && (
          <div>
            <Typography
              component="h3"
              variant="admin_label"
              className="mb-1"
            >
              Users per License
            </Typography>

            <Typography variant="admin_txt2_dim" className="block mt-0.5 mb-2">
              Maximum distinct users that can activate licenses generated from this model. 0 = unlimited (only seats apply).
            </Typography>

            <Input
              variant="admin"
              type="number"
              value={formData.users_per_license}
              onChange={(e) =>
                handleInputChange("users_per_license", e.target.value)
              }
              min={0}
              error={errors.users_per_license}
              className="w-full max-w-xs"
            />
          </div>
        )}

        {/* Validity - Only show for generated mode */}
        {generationMode === "generated" && (
          <div>
            <Typography
              component="h3"
              variant="admin_label"
            >
              License Validity
            </Typography>

            <div className="flex flex-col gap-2 mb-3">
              <Checkbox
                variant="admin"
                isOn={validityType === "lifetime"}
                onChange={() => handleValidityTypeChange("lifetime")}
                label="Lifetime"
              />

              <Checkbox
                variant="admin"
                isOn={validityType === "days"}
                onChange={() => handleValidityTypeChange("days")}
                label="Number of Days"
              />
            </div>

            {validityType === "days" && (
              <Input
                variant="admin"
                type="number"
                value={formData.validity}
                onChange={(e) =>
                  handleInputChange("validity", e.target.value)
                }
                min={1}
                placeholder="365"
                error={errors.validity}
                className="w-full max-w-[200px]"
              />
            )}
          </div>
        )}

        {/* Model Type - External */}
        <div>
          <Typography 
            component="h3"
            variant="admin_label"
          >
            Model Type
          </Typography>

          <div className="flex items-start justify-between">
            <div className="w-[550px]">
              <Typography variant="admin_body" className="my-0">
                External Model
              </Typography>

              <Typography
                variant="admin_txt1"
                className="max-w-[550px] text-admin_text_secondary my-0"
              >
                For partner products or external systems. License generation and tracking occurs outside this API.
              </Typography>
            </div>

            <ToggleSwitch
              variant="admin"
              isOn={formData.external}
              onChange={(isExternal) => handleInputChange("external", isExternal)}
            />
          </div>
        </div>

        {/* Primary Model - Only show for generated mode and non-external models */}
        {generationMode === "generated" && !formData.external && (
          <div>
            <Typography 
              component="h3"
              variant="admin_label"
            >
              Primary Model
            </Typography>

            <div className="flex items-center justify-between">
              <div className="w-[550px]">
                <Typography variant="admin_body" className="my-0">
                  Set as Primary
                </Typography>

                <Typography
                  variant="admin_txt1"
                  className="max-w-[550px] text-admin_text_secondary my-0"
                >
                  System-generated licenses will use this model by default.
                </Typography>
              </div>

              <ToggleSwitch
                variant="admin"
                isOn={formData.primary}
                onChange={(isPrimary) => handleInputChange("primary", isPrimary)}
              />
            </div>
          </div>
        )}

      </div>
    </Modal>
  )
}
